import Auth from 'dashboard/api/auth';
import { normalizeLangChainPayload } from 'shared/helpers/normalizeLangChainPayload';

function getDeviseHeaders() {
  if (!Auth.hasAuthCookie()) return {};
  const {
    'access-token': accessToken,
    'token-type': tokenType,
    client,
    expiry,
    uid,
  } = Auth.getAuthData();
  return {
    'access-token': accessToken,
    'token-type': tokenType,
    client,
    expiry,
    uid,
  };
}

async function normalizeJsonResponse(response) {
  const data = await response.json();
  normalizeLangChainPayload(data);
  return new Response(JSON.stringify(data), {
    status: response.status,
    statusText: response.statusText,
    headers: response.headers,
  });
}

/**
 * Creates a fetch wrapper for direct LangGraph requests.
 * Uses credentials: 'include' for HttpOnly cookie auth; attaches Bearer only
 * when getToken() returns a body token (local COPILOT_JWT_IN_BODY mode).
 */
export function useCopilotFetch({
  apiUrl,
  getToken,
  currentThreadId,
  setLoadingPhase,
  loadingPhases,
  sameOrigin = false,
}) {
  const activeSSEControllers = new Set();

  async function buildHeaders(init) {
    const headers = {
      ...(init?.headers || {}),
    };
    if (sameOrigin) {
      // Rails session (Devise) for Copilot Beta proxy — no LangGraph JWT.
      Object.assign(headers, getDeviseHeaders());
      return headers;
    }
    const token = await getToken();
    // Prefer Bearer when Rails returns a body token (local / until cookie Domain works).
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    return headers;
  }

  function resolveUrl(url) {
    const parsed = new URL(url, apiUrl);
    return parsed.toString();
  }

  function isStreamRequest(headers, url) {
    return (
      headers.Accept === 'text/event-stream' ||
      (typeof url === 'string' && url.includes('/stream'))
    );
  }

  function trackSSEController(init) {
    const controller = new AbortController();
    activeSSEControllers.add(controller);

    if (init?.signal) {
      init.signal.addEventListener(
        'abort',
        () => controller.abort(init.signal.reason),
        { once: true }
      );
    }
    controller.signal.addEventListener(
      'abort',
      () => activeSSEControllers.delete(controller),
      { once: true }
    );

    return controller;
  }

  async function copilotFetch(url, init) {
    const finalUrl = resolveUrl(url);
    const isStream = isStreamRequest(init?.headers || {}, finalUrl);

    if (isStream && setLoadingPhase && loadingPhases) {
      setLoadingPhase(loadingPhases.AUTHENTICATING);
    }

    const headers = await buildHeaders(init);

    if (isStream && setLoadingPhase && loadingPhases) {
      setLoadingPhase(loadingPhases.CONNECTING);
    }

    const fetchInit = {
      ...init,
      headers,
      credentials: 'include',
    };

    if (isStream) {
      const controller = trackSSEController(init);
      fetchInit.signal = controller.signal;
    }

    const response = await fetch(finalUrl, fetchInit);

    if (isStream && response.ok && setLoadingPhase && loadingPhases) {
      setLoadingPhase(loadingPhases.STARTING);
    }

    if (!response.ok) return response;

    // Only rewrite JSON (thread state / history). Do not pipe SSE through a
    // TransformStream — buffering event boundaries leaves the SDK hung with
    // isLoading=true after the run finishes.
    const contentType = response.headers.get('content-type') || '';
    if (
      !isStream &&
      !contentType.includes('text/event-stream') &&
      contentType.includes('application/json')
    ) {
      return normalizeJsonResponse(response);
    }

    return response;
  }

  function abortActiveStreams() {
    activeSSEControllers.forEach(c => c.abort());
    activeSSEControllers.clear();
  }

  return {
    copilotFetch,
    abortActiveStreams,
    getAuthHeaders: getDeviseHeaders,
  };
}

/**
 * Build LangGraph fetch headers + credentials for store helpers.
 * @param {string|null} token - Bearer token when body mode is on
 */
export function copilotRequestInit(token, init = {}) {
  const headers = { ...(init.headers || {}) };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return {
    ...init,
    headers,
    credentials: 'include',
  };
}
